import { Button } from "@alaffia-technology-solutions/ds";

import { IconLocation, IconTrash } from "@tabler/icons-react";

import type { PDFPoint } from "../../helper-types";
import type { PDFAnnotation } from "./types";

import { mapRatioToHighlightCoords } from "../../utils";
import { PDFAnnotationItemActions } from "./pdf-annotation-item-actions";
import { PDFAnnotationsItem } from "./pdf-annotations-item";

interface PDFAnnotationsProps {
  annotations: PDFAnnotation[];
  activeAnnotationId?: string | null;
  onAnnotationClick?: (annotation: PDFAnnotation, offset?: PDFPoint) => unknown;
  onDeleteClick?: (annotation: PDFAnnotation) => unknown;
  getPageDimensions?: (
    pageIndex: number,
  ) => Promise<{ width: number; height: number } | null>;
}

export function PDFAnnotations({
  annotations,
  activeAnnotationId,
  onAnnotationClick,
  onDeleteClick,
  getPageDimensions,
}: PDFAnnotationsProps) {
  const handleAnnotationClick = async (annotation: PDFAnnotation) => {
    if (!onAnnotationClick) {
      return;
    }

    const firstCoord = annotation.coordinates?.[0];
    const pageDimensions = await getPageDimensions?.(annotation.pageIndex - 1);

    let offset;
    if (firstCoord && pageDimensions) {
      offset = mapRatioToHighlightCoords(firstCoord, pageDimensions);
    }
    return onAnnotationClick(
      annotation,
      offset && {
        x: offset.lowerLeft.x,
        y: offset.upperRight.y,
      },
    );
  };

  if (annotations.length === 0) {
    return (
      <p className="af-p-4 af-text-xs">
        No annotations have been created for this document yet. To create one,
        select some text in the PDF.
      </p>
    );
  }

  return (
    <ul className="af-h-full af-overflow-y-auto af-p-2">
      {annotations.map((annotation) => {
        return (
          <PDFAnnotationsItem
            key={annotation.id}
            annotation={annotation}
            active={annotation.id === activeAnnotationId}
          >
            <PDFAnnotationItemActions>
              <Button
                size="icon"
                variant="outline"
                className="af-h-8 af-w-8"
                onClick={() => handleAnnotationClick(annotation)}
              >
                <IconLocation className="af-size-4" />
              </Button>
              <Button
                size="icon"
                variant="destructive"
                className="af-h-8 af-w-8"
                onClick={() => onDeleteClick?.(annotation)}
              >
                <IconTrash className="af-size-4" />
              </Button>
            </PDFAnnotationItemActions>
          </PDFAnnotationsItem>
        );
      })}
    </ul>
  );
}
